// Native cart API only. Never resubmit an uncertain request automatically.
export async function addNativeItem(fetcher, root, formData) {
  const response = await fetcher(`${root}cart/add.js`, {
    method: 'POST', body: formData, credentials: 'same-origin',
    headers: { Accept: 'application/json' }
  });
  let data;
  try { data = await response.json(); } catch {
    throw new Error('We couldn’t confirm the update. View your bag before trying again.');
  }
  if (!response.ok || data.status >= 400) {
    throw new Error(data.description || data.message || 'This item couldn’t be added. Please try again.');
  }
  if (!data.id || !data.product_title || !Number.isSafeInteger(data.quantity) || data.quantity < 1) {
    throw new Error('We couldn’t confirm the update. View your bag before trying again.');
  }
  return data;
}

if (typeof document !== 'undefined') {
  const dialog = document.querySelector('[data-ub-added-dialog]');
  let trigger;
  dialog?.querySelectorAll('[data-ub-added-close]').forEach(button => {
    button.addEventListener('click', () => dialog.close());
  });
  dialog?.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    if (event.clientX < box.left || event.clientX > box.right ||
        event.clientY < box.top || event.clientY > box.bottom) dialog.close();
  });
  dialog?.addEventListener('close', () => trigger?.focus({ preventScroll: true }));
  document.addEventListener('submit', async event => {
    const form = event.target;
    const button = event.submitter;
    // Keep accelerated checkout and unrelated forms under native Shopify control.
    if (!form.matches?.('.pdp-buy form, .product-card form') || !button?.matches('[data-ub-add]') ||
        !dialog?.showModal || typeof fetch !== 'function') return;
    event.preventDefault();
    if (form.dataset.addPending === 'true') return;
    form.dataset.addPending = 'true';
    const data = new FormData(form);
    trigger = button;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    let status = form.closest('.pdp-buy, .product-card')?.querySelector('[data-ub-add-status]');
    if (!status) {
      status = document.createElement('p');
      status.className = 'ub-add-status';
      status.dataset.ubAddStatus = '';
      status.setAttribute('role', 'status');
      form.insertAdjacentElement('afterend', status);
    }
    status.textContent = '';
    const root = window.Shopify?.routes?.root || '/';
    try {
      const item = await addNativeItem(fetch, root, data);
      dialog.querySelector('[data-ub-added-title]').textContent = item.product_title;
      const image = dialog.querySelector('[data-ub-added-image]');
      const src = item.featured_image?.url || item.image;
      image.hidden = !src;
      if (src) { image.src = src; image.alt = item.product_title; }
      dialog.showModal();
      // Use the current native cart count, not the increment or a guessed total.
      try {
        const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          if (Number.isSafeInteger(cart.item_count)) {
            document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
          }
        }
      } catch { /* A confirmed add stays confirmed if count refresh fails. */ }
      document.dispatchEvent(new CustomEvent('ub:cart-updated'));
    } catch (error) {
      status.textContent = /cannot find variant/i.test(error.message || '') ? 'This item is not currently available to add. Please contact us for help.' : error.message || 'We couldn’t confirm the update. View your bag before trying again.';
      status.append(' ');
      const link = document.createElement('a');
      link.href = `${root}cart`;
      link.textContent = 'View bag';
      status.append(link);
    } finally {
      button.disabled = false;
      button.textContent = label;
      delete form.dataset.addPending;
    }
  });
}

if (typeof document !== 'undefined') {
  const zoom = document.querySelector('[data-ub-image-dialog]');
  let zoomTrigger;
  zoom?.querySelector('[data-ub-image-close]')?.addEventListener('click', () => zoom.close());
  zoom?.addEventListener('close', () => zoomTrigger?.focus({preventScroll:true}));
  zoom?.addEventListener('click', event => {
    if (event.target !== zoom) return;
    const box=zoom.getBoundingClientRect();
    if(event.clientX<box.left || event.clientX>box.right || event.clientY<box.top || event.clientY>box.bottom) zoom.close();
  });
  document.addEventListener('click', event => {
    const link = event.target.closest('a[data-ub-image-zoom]');
    if (!link || !zoom?.showModal) return;
    event.preventDefault();
    zoomTrigger=link;
    const image=zoom.querySelector('[data-ub-zoom-image]');
    image.src=link.href;
    image.alt=link.querySelector('img')?.alt || 'Enlarged product image';
    zoom.showModal();
  });
}

// Preserve curated references first; ask Shopify for native related products only as fallback.
if (typeof document !== 'undefined') {
  const related = document.querySelector('[data-ub-related][hidden]');
  if (related?.dataset.url) {
    related.dataset.relatedState='loading';
    const url = new URL(related.dataset.url, location.origin);
    if (url.origin === location.origin) fetch(url, {credentials:'same-origin'}).then(response => {
      if (!response.ok) throw Error('Related products unavailable');
      return response.text();
    }).then(html => {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const results = parsed.querySelector('[data-ub-related-results]');
      if (!results?.querySelector('.product-card')) {related.dataset.relatedState='empty';return;}
      related.replaceChildren(results);
      related.hidden = false;related.dataset.relatedState='ready';
    }).catch(() => { related.dataset.relatedState='error'; });
  }
}

// Keep the mobile highlights short while retaining every product feature.
if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-ub-feature-toggle]').forEach(button => {
    button.addEventListener('click', () => {
      const expanded = button.getAttribute('aria-expanded') !== 'true';
      const list = document.getElementById(button.getAttribute('aria-controls'));
      if (!list) return;
      list.dataset.expanded = String(expanded);
      button.setAttribute('aria-expanded', String(expanded));
      button.replaceChildren(document.createTextNode(expanded ? 'Show fewer features' : `Show all ${button.dataset.featureCount} features`));
      const indicator = document.createElement('span');
      indicator.setAttribute('aria-hidden', 'true');
      indicator.textContent = expanded ? '−' : '+';
      button.append(indicator);
    });
  });
}
